import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Code2, Users, MessageCircle, Activity, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { aggregateCategoryForums } from '../utils/categoryAggregates';
import { buildSoftwareSeoParagraph, latestForumUpdateIso } from '../utils/softwareAggregates';
import { calculateForumScore } from '../utils/forumScore';
import { findSoftwareBySlug, createSoftwareSlug } from '../utils/urlHelpers';
import { absoluteUrl } from '../utils/seo';
import { buildOgImageUrl } from '../utils/ogImage';
import { formatChangePercent } from '../utils/forumStatChange';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

const SoftwareStatsSeoPage: React.FC<Props> = ({ darkMode }) => {
  const { slug } = useParams<{ slug: string }>();
  const [allForums, setAllForums] = useState<ForumBase[]>([]);
  const [softwareName, setSoftwareName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .not('software', 'is', null)
      .then(({ data }) => {
        const list = data || [];
        setAllForums(list);
        const softwareList = [...new Set(list.map((f) => f.software).filter(Boolean))] as string[];
        const decoded = decodeURIComponent(slug || '');
        setSoftwareName(findSoftwareBySlug(decoded, softwareList) || decoded);
        setLoading(false);
      });
  }, [slug]);

  const forums = useMemo(
    () => allForums.filter((f) => f.software === softwareName),
    [allForums, softwareName]
  );

  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastIso = useMemo(() => latestForumUpdateIso(forums), [forums]);
  const marketShare = useMemo(() => {
    const total = allForums.length;
    if (!total) return null;
    return (forums.length / total) * 100;
  }, [allForums.length, forums.length]);

  const topForums = useMemo(
    () =>
      [...forums]
        .sort(
          (a, b) =>
            calculateForumScore(b.topics_count, b.posts_count, b.members_count, b.founded_year) -
            calculateForumScore(a.topics_count, a.posts_count, a.members_count, a.founded_year)
        )
        .slice(0, 10),
    [forums]
  );

  const seoDesc = useMemo(
    () => buildSoftwareSeoParagraph(softwareName, agg, lastIso, null, marketShare),
    [softwareName, agg, lastIso, marketShare]
  );

  const canonical = `/yazilim/${createSoftwareSlug(softwareName)}/istatistikleri`;

  usePageSeo({
    title: `${softwareName} Forum İstatistikleri — Kullanım ve Büyüme`,
    description: seoDesc.slice(0, 320),
    keywords: `${softwareName}, forum yazılımı, ${softwareName} istatistikleri, türk forumları`,
    canonical: absoluteUrl(canonical),
    ogImage: softwareName
      ? buildOgImageUrl({
          title: `${softwareName} İstatistikleri`,
          subtitle: marketShare != null ? `Pazar payı ~%${marketShare.toFixed(1)}` : `${agg.forumCount} forum`,
          line1: topForums[0] ? `#1 ${topForums[0].name}` : undefined,
          badge: 'Yazılım özeti',
        })
      : undefined,
  });

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!softwareName || forums.length === 0) {
    return (
      <div className={`min-h-screen p-8 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <p>Yazılım bulunamadı veya bu yazılımı kullanan aktif forum yok.</p>
        <Link to="/software" className="text-indigo-500 mt-4 inline-block">
          Yazılımlara dön
        </Link>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-4xl">
        <Link
          to={`/software/${createSoftwareSlug(softwareName)}`}
          className={`inline-flex items-center gap-2 mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
        >
          <ArrowLeft size={18} />
          {softwareName} forum listesi
        </Link>

        <header className={`rounded-2xl border p-6 mb-6 ${card}`}>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Code2 className="text-indigo-500" />
            {softwareName} — forum istatistikleri
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {seoDesc}
          </p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { icon: Trophy, label: 'Forum', value: agg.forumCount },
            { icon: Users, label: 'Üye', value: agg.totalMembers },
            { icon: MessageCircle, label: 'Konu', value: agg.totalTopics },
            { icon: Activity, label: 'İleti', value: agg.totalPosts },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className={`rounded-xl border p-4 min-w-0 ${card}`}>
              <Icon size={20} className="text-indigo-500 mb-2" />
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
              <p className="text-xl font-bold tabular-nums">{value.toLocaleString('tr-TR')}</p>
            </div>
          ))}
        </div>

        <section className={`rounded-xl border p-5 ${card}`}>
          <h2 className="text-lg font-semibold mb-4">Bu yazılımı kullanan forumlar (skor)</h2>
          <ol className="space-y-2">
            {topForums.map((f, i) => (
              <li key={f.id} className="flex items-center gap-3">
                <span className="font-bold text-indigo-500 w-6">{i + 1}</span>
                <Link
                  to={`/forum/${f.forum_id}`}
                  className={`font-medium hover:underline flex-1 truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  {f.name}
                </Link>
                <span className="text-sm tabular-nums shrink-0">
                  {calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year).toLocaleString('tr-TR')}
                </span>
              </li>
            ))}
          </ol>
          <Link
            to={`/software/${createSoftwareSlug(softwareName)}`}
            className="inline-block mt-6 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium"
          >
            Tüm {softwareName} forumları
          </Link>
        </section>
      </div>
    </div>
  );
};

export default SoftwareStatsSeoPage;
